import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { motion } from 'framer-motion';
import UrlInput from '../../components/UrlInput';
import VideoInfo from '../../components/VideoInfo';
import DownloadHistory from '../../components/DownloadHistory';
import BatchQueuePanel from '../../components/BatchQueuePanel';
import { VideoMetadata } from '../../services/api';
import { useDownloadHistory } from '../../hooks/useDownloadHistory';
import { useBatchQueue } from '../../hooks/useBatchQueue';

const DownloaderPage: React.FC = () => {
  const [videoInfo, setVideoInfo] = useState<VideoMetadata | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [batchMessage, setBatchMessage] = useState<string | null>(null);
  const [batchSeverity, setBatchSeverity] = useState<
    'success' | 'warning' | 'error' | 'info'
  >('info');
  const { history, addToHistory, clearHistory, deleteItem } = useDownloadHistory();
  const batch = useBatchQueue();
  const queueRef = useRef<HTMLDivElement | null>(null);

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const handleVideoInfo = (info: VideoMetadata) => {
    setVideoInfo(info);
    setVideoUrl(info.url);
    setError(null);
    addToHistory(info);
  };

  const handleBatchUrls = (urls: string[]) => {
    setBatchMessage(null);
    batch.enqueueAndRun(urls);
  };

  useEffect(() => {
    if (batch.items.length === 0) return;
    queueRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [batch.items.length]);

  const handleDownloadZip = async () => {
    try {
      const result = await batch.downloadReadyAsZip('high');
      setBatchMessage(result.message);
      setBatchSeverity(result.saved < result.total ? 'warning' : 'success');
    } catch (err) {
      setBatchMessage(
        err instanceof Error ? err.message : 'Batch download failed'
      );
      setBatchSeverity('error');
    }
  };

  const handleHistoryItemClick = (item: VideoMetadata) => {
    setVideoInfo(item);
    setVideoUrl(item.url);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteItem = (item: VideoMetadata) => {
    deleteItem(item);
    if (videoInfo && videoInfo.url === item.url) {
      setVideoInfo(null);
      setVideoUrl('');
    }
  };

  const handleQueueItemReady = (info: VideoMetadata) => {
    setVideoInfo(info);
    setVideoUrl(info.url);
    setError(null);
    addToHistory(info);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
      }}
    >
      <motion.div variants={itemVariants}>
        <UrlInput
          onVideoInfo={handleVideoInfo}
          setError={setError}
          onBatchUrls={handleBatchUrls}
        />
      </motion.div>

      {batch.items.length > 0 && (
        <motion.div variants={itemVariants} ref={queueRef}>
          <BatchQueuePanel
            items={batch.items}
            isRunning={batch.isRunning}
            isZipping={batch.isZipping}
            zipProgress={batch.zipProgress}
            readyCount={batch.readyCount}
            errorCount={batch.errorCount}
            pendingCount={batch.pendingCount}
            onDownloadZip={handleDownloadZip}
            onClear={() => {
              batch.clearQueue();
              setBatchMessage(null);
            }}
            onRetryFailed={() => {
              setBatchMessage(null);
              batch.retryFailed();
            }}
            onRemove={batch.removeItem}
            onItemReady={handleQueueItemReady}
            message={batchMessage}
            messageSeverity={batchSeverity}
            onDismissMessage={() => setBatchMessage(null)}
          />
        </motion.div>
      )}

      {error && (
        <motion.div variants={itemVariants}>
          <Alert
            severity={error.startsWith('Queued ') ? 'warning' : 'error'}
            className="mb-4"
            onClose={() => setError(null)}
            sx={
              error.startsWith('Queued ')
                ? undefined
                : {
                    backgroundColor: '#2D1115',
                    color: '#F4212E',
                    '& .MuiAlert-icon': {
                      color: '#F4212E',
                    },
                  }
            }
          >
            {error}
          </Alert>
        </motion.div>
      )}
      {videoInfo && (
        <motion.div variants={itemVariants}>
          <VideoInfo
            videoInfo={videoInfo}
            videoUrl={videoUrl}
            setError={setError}
            onDelete={() => handleDeleteItem(videoInfo)}
          />
        </motion.div>
      )}

      <motion.div variants={itemVariants}>
        <DownloadHistory
          history={history}
          onItemClick={handleHistoryItemClick}
          onClearHistory={clearHistory}
          onDeleteItem={handleDeleteItem}
        />
      </motion.div>
    </Box>
  );
};

export default DownloaderPage;
